import { useCallback, useMemo, useRef, useState } from 'react';
import type {
  CandidateRow,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import type { AiImportMappingUnsupportedReason } from '@shared/models/ai-import-mapping.model';
import { getSelectedVersion } from '@client/store/ui';
import { getProductsLookup } from '@client/store/product';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import { resolveAiImportMappingDraft } from '@shared/helpers/ai-import-mapping.helpers';
import { getImportRowDraft } from '@shared/helpers/import-row-draft.helpers';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { collectImportedElementsInView } from './candidate-status.helpers';
import { elementImportsApi } from './element-imports.api';
import { getVisibleListRowIds } from './list-selection.store';
import { useAutoImportCandidates } from './use-auto-import.hook';
import {
  selectAiImportMappingTargetRows,
  snackbarForAiImportMappingBatch,
  type AiImportMappingBatchKind,
} from './ai-import-mapping-apply.helpers';

const EMPTY_IDS: ReadonlySet<string> = new Set();

export type AiImportMappingActions = {
  mapFromRow: (row: CandidateRow) => void;
  isMapping: (rowId: string) => boolean;
  targetCountFromRow: (row: CandidateRow) => number;
};

type UseAiImportMappingArgs = {
  rows: readonly CandidateRow[];
  selectedIds: ReadonlySet<string>;
};

type FetchOutcome = {
  kind: AiImportMappingBatchKind;
  draft?: Partial<ImportRowDraft>;
  unsupportedReason?: AiImportMappingUnsupportedReason;
};

const fetchOutcome = async (row: CandidateRow): Promise<FetchOutcome> => {
  const version = getSelectedVersion();
  const ifcMeasure = version
    ? getImportRowDraft(version, row.id).ifcMeasure
    : undefined;
  try {
    const response = await elementImportsApi.suggestMapping(row.importId, {
      rowId: row.id,
      ifcMeasure,
    });
    const resolved = resolveAiImportMappingDraft(row, ifcMeasure, response);
    switch (resolved.kind) {
      case 'draft':
        return { kind: 'mapped', draft: resolved.draft };
      case 'unsupported':
        return { kind: 'unsupported', unsupportedReason: resolved.reason };
      case 'stale':
      case 'no_match':
      case 'missing_product':
      case 'no_draft':
        return { kind: resolved.kind };
      default:
        return assertUnreachable(resolved);
    }
  } catch {
    return { kind: 'failed' };
  }
};

const applyBatchResults = (
  targets: readonly CandidateRow[],
  results: readonly FetchOutcome[],
  importRowsWithDrafts: ReturnType<
    typeof useAutoImportCandidates
  >['importRowsWithDrafts'],
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
): void => {
  const drafts = results.flatMap((result, index) => {
    const row = targets[index];
    if (!row || result.kind !== 'mapped' || !result.draft) return [];
    return [{ row, patch: result.draft }];
  });
  if (drafts.length > 0) {
    importRowsWithDrafts(drafts, { trigger: 'ai' });
  }
  const [first] = results;
  const productId =
    results.length === 1 ? first?.draft?.referenceId : undefined;
  const mappedProductName = productId
    ? getProductsLookup()[productId]?.name
    : undefined;
  const snackbar = snackbarForAiImportMappingBatch(
    results.map((result) => result.kind),
    results.length === 1 ? first?.unsupportedReason : undefined,
    mappedProductName,
  );
  if (snackbar) {
    enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
  }
};

/**
 * Fetch and apply AI product suggestions for one incomplete row, or every
 * eligible row in the visible multi-selection (DEV-3605). Additional clicks
 * while a batch runs are queued and processed in order.
 */
export const useAiImportMapping = ({
  rows,
  selectedIds,
}: UseAiImportMappingArgs): AiImportMappingActions => {
  const { enqueueSnackbar } = useSnackbar();
  const { importRowsWithDrafts } = useAutoImportCandidates();
  const [inFlightIds, setInFlightIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);
  const [queuedTargetIds, setQueuedTargetIds] =
    useState<ReadonlySet<string>>(EMPTY_IDS);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const queueRef = useRef<string[]>([]);
  const drainRef = useRef<Promise<void> | null>(null);

  const resolveTargets = useCallback(
    (sourceRow: CandidateRow): CandidateRow[] => {
      const version = getSelectedVersion();
      return selectAiImportMappingTargetRows(
        sourceRow,
        rowsRef.current,
        selectedIdsRef.current,
        getVisibleListRowIds(),
        collectImportedElementsInView(version),
        version,
      );
    },
    [],
  );

  const syncQueuedTargetIds = useCallback(() => {
    const targetIds = new Set<string>();
    for (const sourceRowId of queueRef.current) {
      const sourceRow = rowsRef.current.find((row) => row.id === sourceRowId);
      if (!sourceRow) continue;
      for (const target of resolveTargets(sourceRow)) {
        targetIds.add(target.id);
      }
    }
    setQueuedTargetIds(targetIds);
  }, [resolveTargets]);

  const runBatch = useCallback(
    async (sourceRow: CandidateRow): Promise<void> => {
      const targets = resolveTargets(sourceRow);
      if (targets.length === 0) return;

      setInFlightIds(new Set(targets.map((row) => row.id)));
      try {
        const results = await Promise.all(targets.map(fetchOutcome));
        applyBatchResults(
          targets,
          results,
          importRowsWithDrafts,
          enqueueSnackbar,
        );
      } finally {
        setInFlightIds(EMPTY_IDS);
      }
    },
    [enqueueSnackbar, importRowsWithDrafts, resolveTargets],
  );

  const drainQueue = useCallback(async (): Promise<void> => {
    while (queueRef.current.length > 0) {
      const [sourceRowId] = queueRef.current;
      if (!sourceRowId) break;
      queueRef.current.shift();
      syncQueuedTargetIds();

      const sourceRow = rowsRef.current.find((row) => row.id === sourceRowId);
      if (!sourceRow) continue;
      await runBatch(sourceRow);
    }
  }, [runBatch, syncQueuedTargetIds]);

  const mapFromRow = useCallback(
    (sourceRow: CandidateRow) => {
      if (queueRef.current.includes(sourceRow.id)) return;
      queueRef.current.push(sourceRow.id);
      syncQueuedTargetIds();

      if (drainRef.current) return;
      drainRef.current = drainQueue().finally(() => {
        drainRef.current = null;
        syncQueuedTargetIds();
      });
    },
    [drainQueue, syncQueuedTargetIds],
  );

  const isMapping = useCallback(
    (rowId: string) => inFlightIds.has(rowId) || queuedTargetIds.has(rowId),
    [inFlightIds, queuedTargetIds],
  );

  const targetCountFromRow = useCallback(
    (row: CandidateRow) => resolveTargets(row).length,
    [resolveTargets],
  );

  return useMemo(
    () => ({ mapFromRow, isMapping, targetCountFromRow }),
    [isMapping, mapFromRow, targetCountFromRow],
  );
};
